import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import api from '../../services/api.js';
import { useAuth } from '../../context/AuthContext.jsx';
import Logo from '../../components/common/Logo.jsx';
import { useMotionDetection }       from '../../hooks/useMotionDetection.js';
import { useAdaptiveQuality }       from '../../hooks/useAdaptiveQuality.js';
import { useContinuousRecording }   from '../../hooks/useContinuousRecording.js';

export default function CameraModePage() {
  const { t }             = useTranslation();
  const { user }          = useAuth();
  const [devices, setDevices]       = useState([]);
  const [selected, setSelected]     = useState(null);
  const [streaming, setStreaming]     = useState(false);
  const [facing, setFacing]           = useState('environment');
  const [viewerCount, setViewerCount] = useState(0);
  const [status, setStatus]           = useState('idle');
  const [motionEnabled, setMotionEnabled]       = useState(true);
  const [motionCount, setMotionCount]           = useState(0);
  const [continuousRec, setContinuousRec]       = useState(false);
  const [chunksUploaded, setChunksUploaded]     = useState(0);

  const socketRef    = useRef(null);
  const streamRef    = useRef(null);
  const videoRef     = useRef(null);
  const peersRef     = useRef({});
  const retryRef     = useRef(null);
  const retryCount   = useRef(0);

  const handleMotion = useCallback(({ ratio, thumbnail }) => {
    if (!socketRef.current || !selected) return;
    setMotionCount(c => c + 1);
    socketRef.current.emit('motion:detected', {
      streamKey: selected.streamKey,
      thumbnail,
    });
    toast('⚠ Mouvement détecté !', { icon: '🔴', duration: 3000 });
  }, [selected]);

  useMotionDetection({ videoRef, enabled: streaming && motionEnabled, onMotion: handleMotion });

  const [qualityLabel, setQualityLabel] = useState('720p');

  useContinuousRecording({
    streamRef,
    enabled:  streaming && continuousRec && !!selected,
    deviceId: selected?.id,
    onChunkUploaded: () => setChunksUploaded(c => c + 1),
  });

  useAdaptiveQuality({
    streamRef, peersRef, enabled: streaming,
    onQualityChange: (label) => setQualityLabel(label),
  });

  useEffect(() => {
    api.get('/devices').then(r => setDevices(r.data.data || [])).catch(() => {});
    return () => stopStream();
  }, []);

  async function startStream() {
    if (!selected) return toast.error('Sélectionne une caméra');
    setStatus('starting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;

      const token = localStorage.getItem('elvyra_token');
      const socket = io(window.location.origin, {
        auth: { token },
        transports: ['websocket', 'polling'],
      });
      socketRef.current = socket;

      socket.on('connect', () => {
        socket.emit('camera:register', { streamKey: selected.streamKey });
      });

      socket.on('camera:ready', () => {
        setStreaming(true);
        setStatus('streaming');
        toast.success(`Stream actif — ${selected.name}`);
      });

      socket.on('viewer:joined', async ({ viewerId }) => {
        const pc = await createPeer(viewerId, stream, socket);
        peersRef.current[viewerId] = pc;
        setViewerCount(c => c + 1);
      });

      socket.on('webrtc:answer', async ({ senderId, answer }) => {
        const pc = peersRef.current[senderId];
        if (pc) await pc.setRemoteDescription(new RTCSessionDescription(answer));
      });

      socket.on('webrtc:ice-candidate', ({ senderId, candidate }) => {
        const pc = peersRef.current[senderId];
        if (pc) pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => {});
      });

      socket.on('viewer:left', ({ viewerId }) => {
        peersRef.current[viewerId]?.close();
        delete peersRef.current[viewerId];
        setViewerCount(c => Math.max(0, c - 1));
      });

      socket.on('error', e => toast.error(e.message));

      socket.on('disconnect', (reason) => {
        setStreaming(false);
        setStatus('disconnected');
        if (reason !== 'io client disconnect') {
          const delay = Math.min(1000 * 2 ** retryCount.current, 30000);
          retryCount.current += 1;
          toast(`Déconnecté — reconnexion dans ${Math.round(delay/1000)}s`, { icon: '🔄' });
          retryRef.current = setTimeout(() => {
            if (streamRef.current) startStream();
          }, delay);
        }
      });

      socket.on('connect', () => { retryCount.current = 0; });

    } catch (err) {
      setStatus('error');
      toast.error(`Caméra inaccessible : ${err.message}`);
    }
  }

  async function createPeer(viewerId, stream, socket) {
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });
    stream.getTracks().forEach(t => pc.addTrack(t, stream));
    pc.onicecandidate = (e) => {
      if (e.candidate) socket.emit('webrtc:ice-candidate', { targetId: viewerId, candidate: e.candidate });
    };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    socket.emit('webrtc:offer', { targetId: viewerId, offer });
    return pc;
  }

  function stopStream() {
    clearTimeout(retryRef.current);
    retryCount.current = 0;
    streamRef.current?.getTracks().forEach(t => t.stop());
    Object.values(peersRef.current).forEach(pc => pc.close());
    peersRef.current = {};
    socketRef.current?.disconnect();
    socketRef.current = null;
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setStreaming(false);
    setStatus('idle');
    setViewerCount(0);
  }

  function toggleFacing() {
    const next = facing === 'environment' ? 'user' : 'environment';
    setFacing(next);
    if (streaming) {
      stopStream();
      setTimeout(() => startStream(), 300);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#060E1F', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 16px' }}>

      <div style={{ width: '100%', maxWidth: 480 }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <Logo size="sm" showText />
          <span style={{ fontSize: 12, color: 'var(--elvyra-text-secondary)' }}>{user?.name}</span>
        </div>

        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--elvyra-text-primary)', marginBottom: 6 }}>
          📷 Mode Caméra
        </h1>
        <p style={{ fontSize: 13, color: 'var(--elvyra-text-secondary)', marginBottom: 24 }}>
          Transforme ce téléphone en caméra de surveillance
        </p>

        {/* Preview vidéo */}
        <div style={{
          width: '100%', aspectRatio: '16/9', background: '#0A1628',
          borderRadius: 12, overflow: 'hidden', marginBottom: 20,
          border: streaming ? '2px solid var(--elvyra-accent)' : '1px solid var(--elvyra-border)',
          position: 'relative',
        }}>
          <video ref={videoRef} autoPlay muted playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />

          {!streaming && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontSize: 48 }}>📷</span>
              <span style={{ color: 'var(--elvyra-text-muted)', fontSize: 13 }}>Caméra inactive</span>
            </div>
          )}

          {streaming && (
            <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 8 }}>
              <span style={{ background: '#FF3B5C', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, animation: 'pulse 2s infinite' }}>
                ● LIVE
              </span>
              {viewerCount > 0 && (
                <span style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, padding: '3px 10px', borderRadius: 20 }}>
                  👁 {viewerCount}
                </span>
              )}
              <span style={{ background: 'rgba(0,0,0,0.6)', color: 'var(--elvyra-accent)', fontSize: 11, padding: '3px 10px', borderRadius: 20 }}>
                {qualityLabel}
              </span>
            </div>
          )}
        </div>

        {/* Sélection caméra */}
        {!streaming && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', color: 'var(--elvyra-text-secondary)', fontSize: 12, marginBottom: 8 }}>
              Caméra ELVYRA à utiliser
            </label>
            {devices.length === 0 ? (
              <p style={{ color: 'var(--elvyra-danger)', fontSize: 13 }}>
                Aucune caméra configurée — crée-en une dans le Dashboard
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {devices.map(d => (
                  <button key={d.id} onClick={() => setSelected(d)} style={{
                    padding: '12px 16px', borderRadius: 10, textAlign: 'left',
                    background: selected?.id === d.id ? 'rgba(0,212,255,0.12)' : 'var(--elvyra-surface-2)',
                    border: selected?.id === d.id ? '2px solid var(--elvyra-accent)' : '1px solid var(--elvyra-border)',
                    color: 'var(--elvyra-text-primary)', cursor: 'pointer',
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{d.name}</div>
                    {d.location && <div style={{ fontSize: 12, color: 'var(--elvyra-text-secondary)', marginTop: 2 }}>{d.location}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Boutons */}
        <div style={{ display: 'flex', gap: 10 }}>
          {!streaming ? (
            <button onClick={startStream} disabled={!selected || status === 'starting'} style={{
              flex: 1, padding: '14px', borderRadius: 10, border: 'none',
              background: selected ? 'var(--elvyra-accent)' : 'var(--elvyra-surface-2)',
              color: selected ? '#060E1F' : 'var(--elvyra-text-muted)',
              fontWeight: 700, fontSize: 15, cursor: selected ? 'pointer' : 'not-allowed',
            }}>
              {status === 'starting' ? '...' : '▶ Démarrer le stream'}
            </button>
          ) : (
            <button onClick={stopStream} style={{
              flex: 1, padding: '14px', borderRadius: 10, border: 'none',
              background: 'var(--elvyra-danger)', color: '#fff',
              fontWeight: 700, fontSize: 15, cursor: 'pointer',
            }}>
              ⏹ Arrêter le stream
            </button>
          )}

          <button onClick={toggleFacing} style={{
            padding: '14px 16px', borderRadius: 10, border: '1px solid var(--elvyra-border)',
            background: 'var(--elvyra-surface-2)', color: 'var(--elvyra-text-primary)',
            fontSize: 20, cursor: 'pointer',
          }}>
            🔄
          </button>
        </div>

        {/* Contrôles détection mouvement */}
        {streaming && (
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--elvyra-surface-2)', borderRadius: 10, border: '1px solid var(--elvyra-border)' }}>
            <div>
              <span style={{ color: 'var(--elvyra-text-primary)', fontSize: 13, fontWeight: 600 }}>🎯 Détection de mouvement</span>
              {motionCount > 0 && <span style={{ marginLeft: 8, color: 'var(--elvyra-danger)', fontSize: 12 }}>{motionCount} alerte(s)</span>}
            </div>
            <button onClick={() => setMotionEnabled(e => !e)} style={{
              padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
              background: motionEnabled ? 'rgba(0,200,150,0.15)' : 'rgba(100,116,139,0.15)',
              color: motionEnabled ? 'var(--elvyra-success)' : 'var(--elvyra-text-muted)',
            }}>
              {motionEnabled ? 'ON' : 'OFF'}
            </button>
          </div>
        )}

        {/* Toggle enregistrement continu (PRO/BUSINESS) */}
        {streaming && (
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--elvyra-surface-2)', borderRadius: 10, border: `1px solid ${continuousRec ? 'var(--elvyra-danger)' : 'var(--elvyra-border)'}` }}>
            <div>
              <span style={{ color: 'var(--elvyra-text-primary)', fontSize: 13, fontWeight: 600 }}>
                ⏺ Enregistrement continu
              </span>
              {continuousRec && <span style={{ marginLeft: 8, color: 'var(--elvyra-danger)', fontSize: 12 }}>
                {chunksUploaded > 0 ? `${chunksUploaded} chunk(s) sauvegardé(s)` : 'En cours...'}
              </span>}
              <p style={{ margin: '2px 0 0', color: 'var(--elvyra-text-muted)', fontSize: 11 }}>
                Sauvegarde chaque 2min · PRO/BUSINESS
              </p>
            </div>
            <button onClick={() => setContinuousRec(r => !r)} style={{
              padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
              background: continuousRec ? 'rgba(255,59,92,0.15)' : 'rgba(100,116,139,0.15)',
              color: continuousRec ? 'var(--elvyra-danger)' : 'var(--elvyra-text-muted)',
            }}>
              {continuousRec ? '⏺ ON' : 'OFF'}
            </button>
          </div>
        )}

        {/* Lien vers le Dashboard */}
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <a href="/dashboard" style={{ color: 'var(--elvyra-accent)', fontSize: 13, textDecoration: 'none' }}>
            ← Retour au Dashboard (mode viewer)
          </a>
        </div>

      </div>
    </div>
  );
}
